'use client'

import React from 'react'
import { useRouter } from 'next/navigation'

import { useLocale } from '@rewardops/ui'

import { useApi } from '../hooks/use-api'
import ResponsiveComponent from '../responsive-component'

const BrandedCategoryLinkCard = ({ promotionData }: any) => {
  const router = useRouter()
  const {
    promotionalMedia,
    promotionalLink,
    promotionalText,
    backgroundColor,
    textColor,
  } = promotionData

  const onClick = () => {
    if (!promotionalLink) {
      return
    }

    if (promotionalLink?.isRelativeUrl) {
      router.push(promotionalLink?.to)
    } else {
      window?.open(promotionalLink?.to, '_blank')
    }
  }

  return (
    <div
      onClick={onClick}
      className="flex cursor-pointer flex-col rounded-lg border hover:shadow-[0_3px_8px_0px_rgba(0,0,0,0.2)]"
    >
      <div className="h-[80px] w-[120px]">
        {promotionalMedia?.file?.contentType?.includes('image') ? (
          <img
            src={promotionalMedia?.file?.url}
            className="h-full w-full object-contain"
          />
        ) : (
          <video className="h-[80px] w-full object-contain" autoPlay muted loop>
            <source
              src={promotionalMedia?.file?.url}
              type={promotionalMedia?.file?.contentType}
            />
          </video>
        )}
      </div>
      <div
        className="h-full max-w-[120px] lg:rounded-b-lg"
        style={{
          backgroundColor: backgroundColor ? backgroundColor : 'unset',
        }}
      >
        <div
          className="mx-2 overflow-hidden text-ellipsis text-clip text-center text-sm"
          style={{
            color: textColor ? textColor : 'unest',
          }}
        >
          {promotionalText?.length > 50
            ? promotionalText?.substring(0, 50) + '...'
            : promotionalText}
        </div>
      </div>
    </div>
  )
}

type BrandedCategoryLinksProps = {
  setIsMainNavOpen?: (open: boolean) => void
}

const BrandedCategoryLinks: React.FC<BrandedCategoryLinksProps> = ({
  setIsMainNavOpen,
}: BrandedCategoryLinksProps) => {
  const router = useRouter()
  const { api, tenant } = useApi()
  const { locale } = useLocale()

  const { data, isLoading }: { data: any[] | undefined; isLoading: boolean } =
    api.contentful.getByTypeAndFilterByField.useQuery({
      type: 'promotionalCard',
      language: locale ?? tenant?.locale?.default,
      fields: [{ key: 'isBrandedCategoryLink', value: true }],
    })

  if (isLoading || !data) {
    return null
  }

  const redirectAndCloseNav = (link: string) => {
    router?.push(link)
    if (setIsMainNavOpen) {
      setIsMainNavOpen(false)
    }
  }

  const desktopComponent = (
    <div className="flex flex-col gap-4">
      {data.map((promo) => (
        <BrandedCategoryLinkCard key={promo?.name} promotionData={promo} />
      ))}
    </div>
  )

  const mobileComponent = (
    <>
      {data.map((promo) => (
        <div key={promo?.name} className="mx-4 border-b px-2 py-4">
          <div className="flex w-full items-center justify-between">
            <button
              id="nav-bar-shop-categories-button"
              className={
                'text-sm font-semibold capitalize decoration-2 hover:text-tenant-primary-color hover:underline hover:underline-offset-4'
              }
              onClick={() => redirectAndCloseNav(promo?.promotionalLink?.to)}
            >
              {promo?.promotionalText?.length > 20
                ? promo?.promotionalText?.substring(0, 20) + '...'
                : promo?.promotionalText}
            </button>
          </div>
        </div>
      ))}
    </>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { BrandedCategoryLinks }
